import React, { useState, useEffect } from 'react';
import './OurJourney.css';

const OurJourney = () => {
  const [activeTimeline, setActiveTimeline] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const milestones = [
    {
      id: 1,
      year: "2015",
      title: "Company Foundation",
      description: "Started with a vision to revolutionize the industry with innovative solutions and customer-centric approach.",
      icon: "🚀",
      image: "/images/milestone-1.jpg",
      highlight: "Pioneering Start",
      stats: {
        value: "5",
        label: "Team Members",
        value2: "10+",
        label2: "Initial Clients"
      },
      achievements: [
        "Secured first round of funding",
        "Established core technology stack",
        "Built founding team culture"
      ],
      technologies: ["React", "Node.js", "AWS"],
      imageStats: [
        { value: "100%", label: "Growth" },
        { value: "5★", label: "Rating" }
      ]
    },
    {
      id: 2,
      year: "2018",
      title: "Product Launch & Market Expansion",
      description: "Successfully launched our flagship product and expanded into international markets.",
      icon: "🌍",
      image: "/images/milestone-2.jpg",
      highlight: "Global Reach",
      stats: {
        value: "50+",
        label: "Team Size",
        value2: "1M+",
        label2: "Users"
      },
      achievements: [
        "Launched in 10+ countries",
        "Received industry awards",
        "Built partner ecosystem"
      ],
      technologies: ["React Native", "Python", "Google Cloud"],
      imageStats: [
        { value: "500%", label: "User Growth" },
        { value: "99.9%", label: "Uptime" }
      ]
    },
    {
      id: 3,
      year: "2021",
      title: "AI Integration & Innovation",
      description: "Integrated advanced AI capabilities and established ourselves as industry innovators.",
      icon: "🤖",
      image: "/images/milestone-3.jpg",
      highlight: "AI First",
      stats: {
        value: "200+",
        label: "Employees",
        value2: "10M+",
        label2: "Active Users"
      },
      achievements: [
        "Developed proprietary AI algorithms",
        "Patented core technologies",
        "Established R&D center"
      ],
      technologies: ["TensorFlow", "Kubernetes", "Microservices"],
      imageStats: [
        { value: "AI Powered", label: "Platform" },
        { value: "Zero", label: "Downtime" }
      ]
    },
    {
      id: 4,
      year: "2024",
      title: "Industry Leadership & Future Vision",
      description: "Achieved market leadership position and set new standards for innovation and excellence.",
      icon: "🏆",
      image: "/images/milestone-4.jpg",
      highlight: "Market Leader",
      stats: {
        value: "500+",
        label: "Team Worldwide",
        value2: "50M+",
        label2: "Global Users"
      },
      achievements: [
        "Recognized as industry leader",
        "Expanded to 50+ countries",
        "Launched sustainability initiatives"
      ],
      technologies: ["AI/ML", "Blockchain", "IoT"],
      imageStats: [
        { value: "#1", label: "Ranking" },
        { value: "100%", label: "Satisfaction" }
      ]
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleMilestoneClick = (milestoneId) => {
    console.log('Milestone clicked:', milestoneId);
    // Add your milestone click logic here
  };

  const navigateToFullStory = () => {
    // Add navigation logic here
    console.log('Navigate to full story');
  };

  return (
    <section className="journey-evolution-wrapper">
      <div className="journey-evolution-container">
        {/* Enhanced Header */}
        <div className="journey-evolution-header">
          <div className="journey-evolution-badge">
            <span className="journey-evolution-badge-text">Our Evolution Journey</span>
            <div className="journey-evolution-badge-sparkle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15 9L22 10L17 15L18 22L12 18L6 22L7 15L2 10L9 9L12 2Z"/>
              </svg>
            </div>
            <div className="journey-evolution-badge-glow"></div>
          </div>
          
          <h2 className="journey-evolution-title">
            Milestones of <span className="journey-evolution-gradient-text">Growth & Innovation</span>
          </h2>
          
          <p className="journey-evolution-subtitle">
            A decade of excellence, innovation, and transformative achievements that shaped our legacy
          </p>
          
          <div className="journey-evolution-title-underline"></div>
          
          {/* Progress Indicator */}
          <div className="journey-evolution-progress">
            <div className="journey-evolution-progress-bar">
              <div 
                className="journey-evolution-progress-fill" 
                style={{width: `${(activeTimeline / (milestones.length - 1)) * 100}%`}}
              ></div>
            </div>
            <div className="journey-evolution-progress-text">
              <span>Milestone {activeTimeline + 1} of {milestones.length}</span>
            </div>
          </div>
        </div>
        
        {/* Enhanced Timeline */}
        <div className="journey-evolution-timeline">
          <div className="journey-evolution-timeline-glow"></div>
          <div className="journey-evolution-timeline-line"></div>
          
          {milestones.map((milestone, index) => (
            <div 
              key={index} 
              className={`journey-evolution-milestone ${index === activeTimeline ? 'journey-evolution-milestone-active' : ''} ${index % 2 === 0 ? 'journey-evolution-milestone-left' : 'journey-evolution-milestone-right'}`}
              onMouseEnter={() => setActiveTimeline(index)}
              onClick={() => setActiveTimeline(index)}
              style={{ '--delay': `${index * 0.1}s` }}
            >
              {/* Enhanced Marker */}
              <div className="journey-evolution-marker">
                <div className="journey-evolution-marker-outer">
                  <div className="journey-evolution-marker-inner">
                    <span className="journey-evolution-marker-icon">{milestone.icon}</span>
                    <div className="journey-evolution-marker-ripple"></div>
                  </div>
                  <div className="journey-evolution-marker-pulse"></div>
                  <div className="journey-evolution-marker-year">{milestone.year}</div>
                </div>
              </div>
              
              {/* Enhanced Content Side */}
              <div className="journey-evolution-content-side">
                <div className="journey-evolution-card">
                  <div className="journey-evolution-card-shine"></div>
                  <div className="journey-evolution-card-glow"></div>
                  
                  <div className="journey-evolution-card-content">
                    <div className="journey-evolution-card-header">
                      <div className="journey-evolution-year-badge">
                        <span className="journey-evolution-year-text">{milestone.year}</span>
                        <div className="journey-evolution-year-sparkle"></div>
                      </div>
                      
                      {milestone.highlight && (
                        <div className="journey-evolution-highlight-tag">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L15 9L22 10L17 15L18 22L12 18L6 22L7 15L2 10L9 9L12 2Z"/>
                          </svg>
                          <span>{milestone.highlight}</span>
                          <div className="journey-evolution-highlight-shimmer"></div>
                        </div>
                      )}
                      
                      <div className="journey-evolution-milestone-counter">
                        <span>{String(index + 1).padStart(2, '0')}</span>
                      </div>
                    </div>
                    
                    <h3 className="journey-evolution-card-title">{milestone.title}</h3>
                    <p className="journey-evolution-card-description">{milestone.description}</p>
                    
                    {/* Enhanced Stats */}
                    {milestone.stats && (
                      <div className="journey-evolution-stats">
                        <div className="journey-evolution-stat-item">
                          <div className="journey-evolution-stat-icon">📈</div>
                          <div className="journey-evolution-stat-content">
                            <span className="journey-evolution-stat-value">{milestone.stats.value}</span>
                            <span className="journey-evolution-stat-label">{milestone.stats.label}</span>
                          </div>
                        </div>
                        {milestone.stats.value2 && (
                          <div className="journey-evolution-stat-item">
                            <div className="journey-evolution-stat-icon">🚀</div>
                            <div className="journey-evolution-stat-content">
                              <span className="journey-evolution-stat-value">{milestone.stats.value2}</span>
                              <span className="journey-evolution-stat-label">{milestone.stats.label2}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Enhanced Achievements */}
                    {milestone.achievements && (
                      <div className="journey-evolution-achievements">
                        <h4 className="journey-evolution-achievements-title">
                          <span>Key Achievements</span>
                          <div className="journey-evolution-achievements-underline"></div>
                        </h4>
                        <ul className="journey-evolution-achievements-list">
                          {milestone.achievements.map((achievement, idx) => (
                            <li key={idx} className="journey-evolution-achievement-item">
                              <div className="journey-evolution-achievement-check">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              </div>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Technology Tags */}
                    {milestone.technologies && (
                      <div className="journey-evolution-technologies">
                        <h5 className="journey-evolution-technologies-title">Technologies Used</h5>
                        <div className="journey-evolution-tech-tags">
                          {milestone.technologies.map((tech, idx) => (
                            <span key={idx} className="journey-evolution-tech-tag">{tech}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Enhanced Footer */}
                    <div className="journey-evolution-card-footer">
                      <div className="journey-evolution-card-decorline"></div>
                      <div className="journey-evolution-card-actions">
                        <button 
                          className="journey-evolution-learn-more"
                          onClick={() => handleMilestoneClick(milestone.id)}
                        >
                          <span>Explore Journey</span>
                          <div className="journey-evolution-arrow-container">
                            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                              <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </button>
                        
                        <button className="journey-evolution-share">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Image Side */}
              <div className="journey-evolution-image-side">
                <div className="journey-evolution-image-container">
                  <div className="journey-evolution-image-wrapper">
                    <div className="journey-evolution-image-overlay"></div>
                    <div className="journey-evolution-image-frame"></div>
                    <img 
                      src={milestone.image} 
                      alt={milestone.title}
                      className="journey-evolution-image"
                      loading="lazy"
                    />
                    <div className="journey-evolution-image-badge">
                      <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2L12.5 7.5L18 8L14 12L15 18L10 15L5 18L6 12L2 8L7.5 7.5L10 2Z"/>
                      </svg>
                    </div>
                    
                    {/* Image Hover Effect */}
                    <div className="journey-evolution-image-hover">
                      <span>View Details</span>
                    </div>
                  </div>
                  
                  {milestone.imageCaption && (
                    <div className="journey-evolution-image-caption">
                      <div className="journey-evolution-caption-icon">📸</div>
                      <p>{milestone.imageCaption}</p>
                    </div>
                  )}
                  
                  {/* Image Stats */}
                  {milestone.imageStats && (
                    <div className="journey-evolution-image-stats">
                      {milestone.imageStats.map((stat, idx) => (
                        <div key={idx} className="journey-evolution-image-stat">
                          <span className="journey-evolution-image-stat-value">{stat.value}</span>
                          <span className="journey-evolution-image-stat-label">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Enhanced Footer */}
        <div className="journey-evolution-footer">
          <div className="journey-evolution-footer-content">
            <div className="journey-evolution-footer-sparkles">
              <div className="journey-evolution-sparkle"></div>
              <div className="journey-evolution-sparkle"></div>
              <div className="journey-evolution-sparkle"></div>
            </div>
            
            <h3 className="journey-evolution-footer-title">The Evolution Continues</h3>
            <p className="journey-evolution-footer-text">
              Join us in shaping the future of innovation and creating lasting impact together
            </p>
            
            <div className="journey-evolution-footer-stats">
              <div className="journey-evolution-footer-stat">
                <span className="journey-evolution-footer-stat-number">10+</span>
                <span className="journey-evolution-footer-stat-label">Years of Excellence</span>
              </div>
              <div className="journey-evolution-footer-stat">
                <span className="journey-evolution-footer-stat-number">50+</span>
                <span className="journey-evolution-footer-stat-label">Milestones Achieved</span>
              </div>
              <div className="journey-evolution-footer-stat">
                <span className="journey-evolution-footer-stat-number">100K+</span>
                <span className="journey-evolution-footer-stat-label">Lives Impacted</span>
              </div>
            </div>
            
            <div className="journey-evolution-footer-actions">
              <button 
                className="journey-evolution-cta-primary"
                onClick={navigateToFullStory}
              >
                <span>Explore Our Full Story</span>
                <div className="journey-evolution-cta-arrow">→</div>
                <div className="journey-evolution-cta-shimmer"></div>
              </button>
              
              <button className="journey-evolution-cta-secondary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
                <span>Get in Touch</span>
              </button>
            </div>
            
            <div className="journey-evolution-scroll-indicator">
              <div className="journey-evolution-scroll-arrow"></div>
              <span>Scroll to explore more milestones</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurJourney;